/**
 * isWithinStatusButtonWindow — Flutter parity for `isShowStatusButtons`
 * (`scrips_core/lib/utils/utils.dart:152`).
 *
 * Gates the appointment status-transition buttons (Mark As Arrived /
 * Mark As No Show / Mark As Checked In / Unmark* / Confirm / Unmark No Show)
 * on the left edit panel. Despite the Flutter param being named `isToday`,
 * the value fed into it is `isShowStatusButtons(start)`, which is a
 * **3-day window**, NOT a same-day check:
 *
 *   currentDate = dateOnly(now())
 *   endDate     = currentDate + 2 days @ 23:59:59
 *   return dateOnly(start) == currentDate
 *       || (dateOnly(start) > currentDate && dateOnly(start) < endDate)
 *
 * i.e. today, tomorrow, and the day-after-tomorrow all pass; today+3 fails.
 *
 * This differs from the calendar row/popup icon variant, whose React port
 * uses a strict same-day `isToday` (a pre-existing narrower gate). The left
 * panel matches Flutter's real 3-day window here.
 *
 * Timezone: the comparison runs entirely in **device-local time** on both
 * sides — Flutter uses local `dateOnly(DateTime.now())`; this uses
 * `setHours(0,0,0,0)` (local) against `new Date()` (local). There is no
 * cross-stack drift because the appointment `start` ships from the API as a
 * **no-Z local-clock datetime** (backend convention — see CLAUDE.md wire
 * contract), so `new Date(start)` parses it in the browser's local zone,
 * which for the clinic IS the operating timezone (e.g. UTC+4 Abu Dhabi).
 * A `…Z` UTC string would be re-interpreted here, but the wire never sends
 * one for appointment times. `dateToMatch`/`today` are both floored to local
 * midnight, so the boundary is a whole-day comparison (equivalent to
 * `differenceInCalendarDays(start, today) ∈ {0, 1, 2}`), not a wall-clock
 * one — DST/offset can't shift the window by a partial day.
 */
export function isWithinStatusButtonWindow(
  startISO?: string | null,
): boolean {
  if (!startISO) return false;
  const start = new Date(startISO);
  if (Number.isNaN(start.getTime())) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dateOnly = new Date(start);
  dateOnly.setHours(0, 0, 0, 0);

  // currentDate + 2 days @ 23:59:59 (matches Flutter's endDate construction).
  const end = new Date(today);
  end.setDate(end.getDate() + 2);
  end.setHours(23, 59, 59, 999);

  const t = dateOnly.getTime();
  return (
    t === today.getTime() ||
    (t > today.getTime() && t < end.getTime())
  );
}
